import {
  DEPOSIT_CAPTURE_STRATEGY_TEXT,
  DEPOSIT_CAPTURE_STRATEGY_FALLBACK_REASON_TEXT,
  DepositPaymentMode,
  type DepositCaptureStrategy,
  type DepositCaptureStrategyFallbackReason,
  type IDepositOptionText,
} from '@principle-theorem/principle-core/interfaces';

/**
 * Projects one field out of a text map, preserving its totality. The source
 * maps are total `Record`s over their key union, so the compiler still rejects
 * a new capture strategy or fallback reason until its text is written.
 */
function selectText<Key extends string>(
  text: Record<Key, IDepositOptionText>,
  select: (option: IDepositOptionText) => string
): Record<Key, string> {
  const entries = Object.entries<IDepositOptionText>(text) as [
    Key,
    IDepositOptionText,
  ][];
  return entries.reduce(
    (labels, [key, option]) => ({ ...labels, [key]: select(option) }),
    {} as Record<Key, string>
  );
}

/**
 * The at-a-glance label staff see on a deposit. Prefers `staffLabel`, which
 * names the mechanism ("Hold, then charge when you accept") rather than the
 * settings phrasing, which is written as an instruction to whoever is choosing
 * the setting and reads oddly once the choice has been made.
 */
export const DEPOSIT_CAPTURE_STRATEGY_LABELS: Record<
  DepositCaptureStrategy,
  string
> = selectText(
  DEPOSIT_CAPTURE_STRATEGY_TEXT,
  (option) => option.staffLabel ?? option.label
);

export const DEPOSIT_CAPTURE_STRATEGY_TOOLTIPS: Record<
  DepositCaptureStrategy,
  string
> = selectText(DEPOSIT_CAPTURE_STRATEGY_TEXT, (option) => option.description);

export const DEPOSIT_CAPTURE_STRATEGY_FALLBACK_LABELS: Record<
  DepositCaptureStrategyFallbackReason,
  string
> = selectText(
  DEPOSIT_CAPTURE_STRATEGY_FALLBACK_REASON_TEXT,
  (option) => option.label
);

export const DEPOSIT_CAPTURE_STRATEGY_FALLBACK_DESCRIPTIONS: Record<
  DepositCaptureStrategyFallbackReason,
  string
> = selectText(
  DEPOSIT_CAPTURE_STRATEGY_FALLBACK_REASON_TEXT,
  (option) => option.description
);

/**
 * Shown when a deposit has no recorded capture strategy. Names what the
 * provider did to the card, which is all the payment mode knows.
 */
export const DEPOSIT_PAYMENT_MODE_LABELS: Record<DepositPaymentMode, string> = {
  [DepositPaymentMode.Charge]: 'Charge',
  [DepositPaymentMode.PreAuth]: 'Card hold',
};
